import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Lock, Key, Globe, 
  Check, ArrowRight, Wallet, Server, Fingerprint,
  ShieldCheck, RefreshCw, Eye, AlertTriangle,
  ChevronDown
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WalletConnectModal from '../components/WalletConnectModal';
import { useAuth } from '../App';
import { getWalletConnectErrorMessage } from '../services/apiService';
import toast from 'react-hot-toast';
import './LedgerService.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const securityFeatures = [
  {
    icon: Lock,
    title: 'End-to-End Encryption',
    description: 'AES-256 encryption protects your data at rest and in transit. Your keys are never exposed.'
  },
  {
    icon: Fingerprint,
    title: 'Biometric Authentication',
    description: 'Add an extra layer of security with fingerprint or face recognition on supported devices.'
  },
  {
    icon: Server,
    title: 'Cold Storage',
    description: 'Critical assets stored in air-gapped systems, completely isolated from internet threats.'
  },
  {
    icon: ShieldCheck,
    title: 'Multi-Signature',
    description: 'Require multiple approvals for high-value transactions, preventing unauthorized access.'
  },
  {
    icon: RefreshCw,
    title: 'Automatic Backup',
    description: 'Encrypted backups ensure you never lose access to your assets, even if devices are lost.'
  },
  {
    icon: Eye,
    title: '24/7 Monitoring',
    description: 'Real-time threat detection and anomaly monitoring protect against suspicious activity.'
  },
];

const supportedAssets = [
  { name: 'Bitcoin', logo: '/Crypto-logos/Bitcoin-Logo.webp', chains: ['Bitcoin'] },
  { name: 'Ethereum', logo: '/Crypto-logos/Ethereum-logo.webp', chains: ['Ethereum', 'Layer 2s'] },
  { name: 'BNB', logo: '/Crypto-logos/bnb-logo.webp', chains: ['BNB Chain'] },
  { name: 'Solana', logo: '/Crypto-logos/Solana-logo.webp', chains: ['Solana'] },
  { name: 'Polygon', logo: '/Crypto-logos/matic-token-icon.webp', chains: ['Polygon'] },
  { name: 'Polkadot', logo: '/Crypto-logos/Polkadot-logo.webp', chains: ['Polkadot'] },
  { name: 'Arbitrum', logo: '/Crypto-logos/Arbitrum-logo.webp', chains: ['Arbitrum'] },
  { name: 'Optimism', logo: '/Crypto-logos/Optimism.webp', chains: ['Optimism'] },
];

const comparisonFeatures = [
  { 
    feature: 'Data Privacy',
    icon: Eye,
    ftmx: 'Your data stays yours - completely private',
    web2: 'Third parties access & track your data',
    ftmxGood: true,
    web2Good: false
  },
  { 
    feature: 'Transaction Tracking',
    icon: Globe,
    ftmx: 'Untraceable & anonymous transactions',
    web2: 'All transactions monitored & recorded',
    ftmxGood: true,
    web2Good: false
  },
  { 
    feature: 'Account Control',
    icon: Lock,
    ftmx: 'You have 100% control - no one can freeze',
    web2: 'Can be suspended, frozen, or blocked',
    ftmxGood: true,
    web2Good: false
  },
  { 
    feature: 'Fund Access',
    icon: Wallet,
    ftmx: 'Available online & offline, anytime',
    web2: 'Depends on servers & internet',
    ftmxGood: true,
    web2Good: false
  },
  { 
    feature: 'Inheritance',
    icon: Key,
    ftmx: 'Family can access via recovery phrase',
    web2: 'Funds may be lost forever',
    ftmxGood: true,
    web2Good: false
  },
  { 
    feature: 'Security',
    icon: Shield,
    ftmx: 'QFS Technology - quantum-proof encryption',
    web2: 'Vulnerable to hacks & breaches',
    ftmxGood: true,
    web2Good: false
  },
];

const LedgerService = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { connectWalletUser } = useAuth();
  const navigate = useNavigate();

  const handleWalletConnect = async (walletData) => {
    try {
      const result = await connectWalletUser(walletData);
      if (result?.error === 'suspended') {
        setIsModalOpen(false);
        toast.error('Your account has been suspended. Please contact support.');
        return;
      }

      setIsModalOpen(false);
      if (result?.isNewUser) {
        toast.success('Wallet connected successfully!');
        navigate('/backup-success');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(getWalletConnectErrorMessage(error));
    }
  };

  return (
    <div className="ledger-page">
      <Navbar />

      {/* Hero Section */}
      <section className="ledger-hero">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        
        <div className="container">
          <motion.div 
            className="hero-content"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
          >
            <motion.div className="hero-badge" variants={fadeInUp}>
              <Shield size={14} />
              <span>Fair & Secure</span>
            </motion.div>

            <motion.h1 variants={fadeInUp}>
              Ledger Security
              <br />
              <span className="text-gradient">Reimagined</span>
            </motion.h1>

            <motion.p className="hero-description" variants={fadeInUp}>
              Secure your holdings with fair-launch principles and uncompromising privacy.
              Our proprietary ledger technology keeps you in control — not the matrix.
            </motion.p>

            <motion.div className="hero-actions" variants={fadeInUp}>
              <Link to="/get-started" className="btn btn-primary btn-lg">
                Get Started
                <ArrowRight size={20} />
              </Link>
              <button 
                className="btn btn-secondary btn-lg"
                onClick={() => setIsModalOpen(true)}
              >
                <Wallet size={20} />
                Connect Wallet
              </button>
            </motion.div>

            <motion.div className="hero-stats" variants={fadeInUp}>
              <div className="hero-stat">
                <span className="stat-value">$4.5B+</span>
                <span className="stat-label">Assets Protected</span>
              </div>
              <div className="stat-divider"></div>
              <div className="hero-stat">
                <span className="stat-value">0</span>
                <span className="stat-label">Security Breaches</span>
              </div>
              <div className="stat-divider"></div>
              <div className="hero-stat">
                <span className="stat-value">99.99%</span>
                <span className="stat-label">Uptime</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-ledger-works">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">How It Works</span>
            <h2>Security Without<br />Compromise</h2>
            <p>Experience the perfect balance between maximum security and ease of use.</p>
          </motion.div>

          <div className="how-it-works-visual">
            <motion.div 
              className="work-step"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="step-icon">
                <Wallet size={28} />
              </div>
              <div className="step-content">
                <h3>Connect Your Wallet</h3>
                <p>Link your existing wallet or create a new one with our secure infrastructure.</p>
              </div>
            </motion.div>

            <div className="work-connector"></div>

            <motion.div 
              className="work-step"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="step-icon">
                <Lock size={28} />
              </div>
              <div className="step-content">
                <h3>Encryption & Backup</h3>
                <p>Your credentials are encrypted with AES-256 and backed up securely.</p>
              </div>
            </motion.div>

            <div className="work-connector"></div>

            <motion.div 
              className="work-step"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="step-icon">
                <ShieldCheck size={28} />
              </div>
              <div className="step-content">
                <h3>Protected & Ready</h3>
                <p>Access your assets anytime while enjoying bank-grade protection.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="security-features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Security Features</span>
            <h2>Military-Grade<br />Protection</h2>
            <p>Every layer designed to keep your assets safe from any threat.</p>
          </motion.div>

          <div className="features-grid">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="security-feature-card glass-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Assets */}
      <section className="supported-assets">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Supported Assets</span>
            <h2>50+ Chains<br />Supported</h2>
            <p>Manage all your assets across multiple blockchains in one secure place.</p>
          </motion.div>

          {/* Desktop Grid - No duplicates */}
          <div className="assets-grid assets-grid-desktop">
            {supportedAssets.map((asset, index) => (
              <motion.div
                key={index}
                className="asset-card glass-card"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="asset-logo">
                  <img src={asset.logo} alt={asset.name} />
                </div>
                <div className="asset-info">
                  <span className="asset-name">{asset.name}</span>
                  <span className="asset-chains">{asset.chains.join(', ')}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Grid - Duplicated for carousel */}
          <div className="assets-grid assets-grid-mobile">
            {[...supportedAssets, ...supportedAssets].map((asset, index) => (
              <motion.div
                key={index}
                className="asset-card glass-card"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: (index % supportedAssets.length) * 0.05 }}
              >
                <div className="asset-logo">
                  <img src={asset.logo} alt={asset.name} />
                </div>
                <div className="asset-info">
                  <span className="asset-name">{asset.name}</span>
                  <span className="asset-chains">{asset.chains.join(', ')}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="more-assets"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span>+42 more chains supported including all major Layer 2s and EVM-compatible networks</span>
          </motion.div>
        </div>
      </section>

      {/* Comparison */}
      <section className="comparison-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">FTMX vs Centralized</span>
            <h2>Why Go<br />Decentralized?</h2>
            <p>True ownership beats centralized control. Fair. Secure. Unstoppable.</p>
          </motion.div>

          {/* Column Headers */}
          <div className="comparison-headers">
            <div className="comparison-header-spacer"></div>
            <motion.div 
              className="comparison-header ftmx-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="header-badge">Recommended</div>
              <h3>FTMX</h3>
              <p>Decentralized & Secure</p>
            </motion.div>
            <motion.div 
              className="comparison-header web2-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3>Web2 Wallets</h3>
              <p>Centralized & Risky</p>
            </motion.div>
          </div>

          {/* Comparison Rows */}
          <div className="comparison-rows">
            {comparisonFeatures.map((item, index) => (
              <motion.div 
                key={index}
                className="comparison-row"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="comparison-feature">
                  <div className="feature-icon-wrap">
                    <item.icon size={20} />
                  </div>
                  <span>{item.feature}</span>
                </div>
                <div className="comparison-cell ftmx-cell">
                  <Check size={18} className="cell-check" />
                  <span>{item.ftmx}</span>
                </div>
                <div className="comparison-cell web2-cell">
                  <span className="cell-x">✕</span>
                  <span>{item.web2}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div 
            className="comparison-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p>Ready to break free from the matrix?</p>
            <button 
              className="btn btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              <Wallet size={18} />
              Get Started with FTMX
            </button>
          </motion.div>
        </div>
      </section>

      {/* Security Warning */}
      <section className="security-warning">
        <div className="container">
          <motion.div 
            className="warning-card glass-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="warning-icon">
              <AlertTriangle size={32} />
            </div>
            <div className="warning-content">
              <h3>Security Reminder</h3>
              <p>
                Never share your recovery phrase or private key with anyone. 
                FTMX encrypts your credentials but will never ask you to 
                reveal them outside of the secure connection process.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="ledger-faq">
        <div className="container">
          <motion.div
            className="faq-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about FTMX security</p>
          </motion.div>

          <div className="faq-grid">
            {[
              {
                question: "Why choose FTMX over traditional wallets?",
                answer: "Unlike Web2 wallets that share your data with third parties and can track your transactions, FTMX is fully decentralized. No one - not even us - has access to your funds or personal information. Your transactions remain private and untraceable, giving you complete financial freedom."
              },
              {
                question: "How does FTMX protect my assets?",
                answer: "We use cutting-edge Quantum Financial System (QFS) technology combined with military-grade encryption. Your private keys never leave your device and are never stored on any server. This makes your assets virtually unhackable and completely under your control."
              },
              {
                question: "What happens if something goes wrong with my account?",
                answer: "Unlike centralized Web2 wallets where your account can be suspended, frozen, or hacked - with FTMX, YOU are in full control. No third party can block your access or freeze your funds. Your assets are available both online and offline, anytime you need them."
              },
              {
                question: "Can my family access my funds if something happens to me?",
                answer: "Yes! With traditional banks and Web2 wallets, your money could be lost forever if something happens to you. FTMX's recovery phrase system ensures your loved ones can securely access your assets. Your wealth stays in your family, not locked away by corporations."
              },
              {
                question: "What cryptocurrencies does FTMX support?",
                answer: "We support all major cryptocurrencies including Bitcoin, Ethereum, and all ERC-20 tokens. We also support Solana, Polygon, BNB Chain, Avalanche, and over 10,000 other tokens across multiple blockchains - all secured with QFS technology."
              },
              {
                question: "How do I contact support if I have issues?",
                answer: "Our support team is available 24/7 through live chat in the app, email at support@ftmx.com, or through our WhatsApp community. Premium users get priority support with guaranteed response times under 2 hours."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <details className="faq-details">
                  <summary className="faq-question">
                    <span>{faq.question}</span>
                    <ChevronDown className="faq-icon" size={20} />
                  </summary>
                  <p className="faq-answer">{faq.answer}</p>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Desktop Two Column Layout */}
      <section className="ledger-cta-section">
        {/* Animated Background */}
        <div className="cta-bg-animation">
          <div className="cta-bg-orb cta-bg-orb-1"></div>
          <div className="cta-bg-orb cta-bg-orb-2"></div>
          <div className="cta-bg-orb cta-bg-orb-3"></div>
          <div className="cta-bg-grid"></div>
        </div>
        
        <div className="container">
          <div className="cta-two-column">
            {/* Left Column - Text Content */}
            <motion.div 
              className="cta-left-column"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="cta-headline">
                Ready to secure your<br />digital future?
              </h2>
              <p className="cta-subheadline">
                Join thousands of users who trust FTMX for their crypto asset management.
              </p>
              <button 
                className="btn btn-primary btn-lg cta-connect-btn"
                onClick={() => setIsModalOpen(true)}
              >
                <Wallet size={20} />
                Connect Wallet
              </button>
            </motion.div>
            
            {/* Right Column - Image */}
            <motion.div 
              className="cta-right-column"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src="/hero_visual.webp" 
                alt="FTMX — Fair launch token" 
                className="cta-hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />

      <WalletConnectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConnect={handleWalletConnect}
        mode="register"
      />
    </div>
  );
};

export default LedgerService;
